import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  BulkContactBody,
  ContactFilter,
  ContactListSort,
  ContactProfileValues,
  CreateContactBody,
  LifecycleChangeBody,
  MergeFieldChoices,
  NoteBody,
  ReferrerPartnerOptionList,
  ReferrerSubmitterList,
  SetDncBody,
  UpdateContactBody,
} from '@brimo/shared';
import { ApiError, api, apiDownload } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type {
  AppointmentDetail,
  BoardResponse,
  BookAppointmentBody,
  BulkResult,
  CompleteTaskResponse,
  ConnectionItem,
  ContactCounts,
  ContactFinancing,
  ContactListResponse,
  ContactProfileResponse,
  ContactProfileVersionDetail,
  ContactProfileVersionsResponse,
  ContactRecord,
  CreateMessageBody,
  CreateTaskBody,
  CreatedContact,
  DncPreview,
  DocumentRequestBody,
  DocumentRequestResponse,
  DocumentType,
  DocumentsResponse,
  DuplicateCompare,
  DuplicatesResponse,
  EventType,
  LifecycleResponse,
  LockedDetails,
  LogCallBody,
  LogCallResponse,
  MergeResponse,
  MessageItem,
  SelfDisclosuresResponse,
  SendSelfDisclosureBody,
  SendSelfDisclosureResponse,
  SlotsResponse,
  TaskDetail,
  TaskNextStep,
  TimelineFilter,
  TimelineResponse,
  WhatsAppStatus,
} from './types';

/** Query keys of the module, all under the roots of `lib/events.ts` so realtime events refresh them. */
export const contactKeys = {
  list: (filter: ContactFilter, sort: string) => [...queryRoots.contacts, 'list', filter, sort] as const,
  counts: (filter: Omit<ContactFilter, 'lifecycle'>) => [...queryRoots.contacts, 'counts', filter] as const,
  board: (range: ContactEntryRange) => [...queryRoots.contacts, 'board', range] as const,
  sources: () => [...queryRoots.contacts, 'sources'] as const,
  referrers: () => [...queryRoots.contacts, 'referrers'] as const,
  duplicates: (params: Record<string, string>) => [...queryRoots.contacts, 'duplicates', params] as const,
  compare: (id: string, choices: Partial<MergeFieldChoices>) =>
    [...queryRoots.contacts, 'compare', id, choices] as const,
  record: (id: string) => [...queryRoots.contact, id] as const,
  timeline: (id: string, filter: TimelineFilter) => [...queryRoots.contact, id, 'timeline', filter] as const,
  dncPreview: (id: string) => [...queryRoots.contact, id, 'dnc-preview'] as const,
  profile: (id: string) => [...queryRoots.contact, id, 'profile'] as const,
  profileVersions: (id: string) => [...queryRoots.contact, id, 'profile', 'versions'] as const,
  profileVersion: (id: string, version: number) =>
    [...queryRoots.contact, id, 'profile', 'versions', version] as const,
  documents: (id: string) => [...queryRoots.documents, 'contact', id] as const,
  documentTypes: () => [...queryRoots.documents, 'types'] as const,
  messages: (id: string, channel: string | null) => [...queryRoots.messages, 'contact', id, channel] as const,
  selfDisclosures: (id: string) => [...queryRoots.selfDisclosures, id] as const,
  eventTypes: () => [...queryRoots.eventTypes] as const,
  slots: (params: Record<string, string>) => [...queryRoots.slots, params] as const,
  task: (id: string) => [...queryRoots.tasks, 'detail', id] as const,
};

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
}

export function cleanFilter(filter: ContactFilter): ContactFilter {
  const result: ContactFilter = {};
  if (filter.q) result.q = filter.q;
  if (filter.lifecycle) result.lifecycle = filter.lifecycle;
  if (filter.owner) result.owner = filter.owner;
  if (filter.source) result.source = filter.source;
  if (filter.referrerPartnerId) result.referrerPartnerId = filter.referrerPartnerId;
  if (filter.referrerPartnerId && filter.referrerSubmitter)
    result.referrerSubmitter = filter.referrerSubmitter;
  if (filter.createdFrom) result.createdFrom = filter.createdFrom;
  if (filter.createdUntil) result.createdUntil = filter.createdUntil;
  if (filter.financing) result.financing = filter.financing;
  return result;
}

// --- list, board, counts -------------------------------------------------------------------------

export const LIST_PAGE_SIZE = 50;

export function useContactList(filter: ContactFilter, sort: ContactListSort = 'name') {
  const clean = cleanFilter(filter);
  return useInfiniteQuery({
    queryKey: contactKeys.list(clean, sort),
    queryFn: ({ pageParam, signal }) =>
      api<ContactListResponse>(
        `/contacts${query({ ...clean, sort, cursor: pageParam ?? undefined, limit: String(LIST_PAGE_SIZE) })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useContactCounts(filter: Omit<ContactFilter, 'lifecycle'>) {
  const clean = cleanFilter(filter);
  return useQuery({
    queryKey: contactKeys.counts(clean),
    queryFn: ({ signal }) => api<ContactCounts>(`/contacts/counts${query(clean)}`, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** The entry date range („Zeitraum", FB-003) — the only list filter the board respects. */
export type ContactEntryRange = Pick<ContactFilter, 'createdFrom' | 'createdUntil'>;

export function useContactBoard(enabled: boolean, range: ContactEntryRange = {}) {
  const clean = cleanFilter(range);
  return useQuery({
    queryKey: contactKeys.board(clean),
    queryFn: ({ signal }) => api<BoardResponse>(`/contacts/board${query(clean)}`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

/**
 * The organization's active referrer partners as options of the „Tippgeber-Partner" filter (D-248). The key sits
 * under `['organization', 'referrer-partners']`, which `organization.updated` refreshes with the settings page.
 */
export function useReferrerPartnerOptions(enabled = true) {
  return useQuery({
    queryKey: ['organization', 'referrer-partners', 'options'] as const,
    queryFn: ({ signal }) =>
      api<ReferrerPartnerOptionList>('/settings/organization/referrer-partners/options', { signal }),
    select: (data) => data.items,
    enabled,
    staleTime: 300_000,
  });
}

/**
 * The submitters („Einreicher", owner decision 02.11.2026) of one referrer partner — the options of the second filter
 * „Alle Einreicher" next to „Tippgeber-Partner". Same key root as the options, so realtime refreshes both.
 */
export function useReferrerSubmitterOptions(partnerId: string) {
  return useQuery({
    queryKey: ['organization', 'referrer-partners', 'submitters', partnerId] as const,
    queryFn: ({ signal }) =>
      api<ReferrerSubmitterList>(`/settings/organization/referrer-partners/${partnerId}/submitters`, {
        signal,
      }),
    select: (data) => data.items,
    enabled: partnerId !== '',
    staleTime: 60_000,
  });
}

export function useContactSources(enabled = true) {
  return useQuery({
    queryKey: contactKeys.sources(),
    queryFn: () => api<{ sources: string[] }>('/contacts/sources'),
    enabled,
    staleTime: 300_000,
  });
}

export function useContactReferrers(enabled = true) {
  return useQuery({
    queryKey: contactKeys.referrers(),
    queryFn: () => api<{ referrers: { id: string; name: string }[] }>('/contacts/referrers'),
    enabled,
    staleTime: 300_000,
  });
}

export interface DuplicateCheckParams {
  phone?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  excludeId?: string;
}

/** The live duplicate check of the create dialog (Kontakte 04); disabled while nothing is typed. */
export function useDuplicateCheck(params: DuplicateCheckParams, enabled: boolean) {
  const clean: Record<string, string> = {};
  for (const [key, value] of Object.entries(params) as [string, string | undefined][])
    if (value) clean[key] = value;
  return useQuery({
    queryKey: contactKeys.duplicates(clean),
    queryFn: ({ signal }) => api<DuplicatesResponse>(`/contacts/duplicates${query(clean)}`, { signal }),
    enabled: enabled && Object.keys(clean).length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export function createContact(body: CreateContactBody): Promise<CreatedContact> {
  return api<CreatedContact>('/contacts', { method: 'POST', body });
}

export function bulkContacts(body: BulkContactBody): Promise<BulkResult> {
  return api<BulkResult>('/contacts/bulk', { method: 'POST', body });
}

export function exportContacts(body: { ids?: string[]; filter?: ContactFilter }): Promise<string> {
  return apiDownload('/contacts/export', body, 'kontakte.csv');
}

// --- the record ----------------------------------------------------------------------------------

/** The locked 403 of a contact outside the visibility scope (Kontaktakte I.10), or null. */
export function lockedDetailsOf(error: unknown): LockedDetails | null {
  if (!(error instanceof ApiError) || error.status !== 403 || error.details?.['locked'] !== true) return null;
  return {
    name: error.detailString('name') ?? '',
    ownerName: error.detailString('ownerName'),
    teamName: error.detailString('teamName'),
  };
}

export function useContactRecord(id: string): UseQueryResult<ContactRecord, Error> {
  return useQuery({
    queryKey: contactKeys.record(id),
    queryFn: ({ signal }) => api<ContactRecord>(`/contacts/${id}`, { signal }),
    retry: (count, error) =>
      !(error instanceof ApiError && (error.status === 403 || error.status === 404)) && count < 1,
    staleTime: 15_000,
  });
}

/** „Jetzt berechnen“ / „Neu berechnen“ of the financing band (D-380). */
export function calculateFinancing(id: string): Promise<ContactFinancing> {
  return api<ContactFinancing>(`/contacts/${id}/financing/calculate`, { method: 'POST' });
}

export function useTimeline(id: string, filter: TimelineFilter, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: contactKeys.timeline(id, filter),
    queryFn: ({ pageParam, signal }) =>
      api<TimelineResponse>(
        `/contacts/${id}/timeline${query({
          channel: filter.channel ?? undefined,
          kind: filter.kind ?? undefined,
          origin: filter.origin ?? undefined,
          cursor: pageParam ?? undefined,
          limit: '50',
        })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    enabled,
    staleTime: 15_000,
  });
}

export function useDncPreview(id: string, enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.dncPreview(id),
    queryFn: () => api<DncPreview>(`/contacts/${id}/dnc-preview`),
    enabled,
    staleTime: 0,
  });
}

export function updateContact(
  id: string,
  body: UpdateContactBody,
): Promise<{ record: ContactRecord | null; handedOver: boolean }> {
  return api(`/contacts/${id}`, { method: 'PATCH', body });
}

// --- profile („Alle Eigenschaften") -----------------------------------------------------------------
// The keys live under the record's root, so `useInvalidateContact` and realtime events refresh them too.

export function useContactProfile(id: string, enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.profile(id),
    queryFn: ({ signal }) => api<ContactProfileResponse>(`/contacts/${id}/profile`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useContactProfileVersions(id: string, enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.profileVersions(id),
    queryFn: ({ signal }) =>
      api<ContactProfileVersionsResponse>(`/contacts/${id}/profile/versions`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function useContactProfileVersion(id: string, version: number | null) {
  return useQuery({
    queryKey: contactKeys.profileVersion(id, version ?? 0),
    queryFn: ({ signal }) =>
      api<ContactProfileVersionDetail>(`/contacts/${id}/profile/versions/${version ?? 0}`, { signal }),
    enabled: version !== null,
    staleTime: 300_000,
  });
}

/** Saves the changed keys of one group; answers the fresh profile payload. */
export function updateContactProfile(
  id: string,
  body: ContactProfileValues,
): Promise<ContactProfileResponse> {
  return api<ContactProfileResponse>(`/contacts/${id}/profile`, { method: 'PATCH', body });
}

export function changeLifecycle(id: string, body: LifecycleChangeBody): Promise<LifecycleResponse> {
  return api<LifecycleResponse>(`/contacts/${id}/lifecycle`, { method: 'POST', body });
}

export function setDnc(
  id: string,
  body: SetDncBody,
): Promise<DncPreview & { gdprApprovalId: string | null }> {
  return api(`/contacts/${id}/dnc`, { method: 'POST', body });
}

export function requestContactAccess(id: string): Promise<{ approvalId: string }> {
  return api(`/contacts/${id}/request-access`, { method: 'POST', body: {} });
}

export function logCall(id: string, body: LogCallBody): Promise<LogCallResponse> {
  return api<LogCallResponse>(`/contacts/${id}/calls`, { method: 'POST', body });
}

export function createNote(id: string, body: NoteBody): Promise<{ id: string }> {
  return api(`/contacts/${id}/notes`, { method: 'POST', body });
}

/** Records the opened record for the search palette's "Zuletzt geöffnet"; never blocks the page. */
export function recordContactOpened(id: string): void {
  api<void>('/search/opened', { method: 'POST', body: { type: 'contact', id } }).catch((error: unknown) => {
    console.warn('search/opened failed', error);
  });
}

// --- duplicates (Kontakte 08) --------------------------------------------------------------------

export function useDuplicateCompare(id: string, choices: Partial<MergeFieldChoices>) {
  return useQuery({
    queryKey: contactKeys.compare(id, choices),
    queryFn: ({ signal }) =>
      api<DuplicateCompare>(`/duplicates/${id}${query(choices as Record<string, string | undefined>)}`, {
        signal,
      }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function mergeDuplicate(id: string, fields: MergeFieldChoices): Promise<MergeResponse> {
  return api<MergeResponse>(`/duplicates/${id}/merge`, { method: 'POST', body: { fields } });
}

export function keepDuplicateSeparate(id: string): Promise<{ candidateId: string; status: 'kept_separate' }> {
  return api(`/duplicates/${id}/keep-separate`, { method: 'POST', body: {} });
}

export function undoMerge(mergeId: string): Promise<{ contactId: string; targetId: string }> {
  return api(`/merges/${mergeId}/undo`, { method: 'POST', body: {} });
}

// --- messages ------------------------------------------------------------------------------------

export function useContactMessages(id: string, channel: 'email' | 'whatsapp' | null, enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.messages(id, channel),
    queryFn: ({ signal }) =>
      api<{ items: MessageItem[]; nextCursor: string | null }>(
        `/messages${query({ contactId: id, channel: channel ?? undefined, limit: '50' })}`,
        { signal },
      ),
    enabled,
    staleTime: 15_000,
  });
}

/** The user's mailbox / WhatsApp session for the editors' title lines (same keys as the settings module). */
export function useMailConnection(enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.connections] as const,
    queryFn: () => api<{ items: ConnectionItem[] }>('/connections'),
    enabled,
    staleTime: 60_000,
    select: (data) =>
      data.items.find((item) => item.purposes.includes('mail') && item.status === 'connected') ?? null,
  });
}

export function useWhatsAppConnection(enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.connections, 'whatsapp'] as const,
    queryFn: () => api<WhatsAppStatus>('/connections/whatsapp'),
    enabled,
    staleTime: 60_000,
  });
}

export function sendMessage(body: CreateMessageBody): Promise<MessageItem> {
  return api<MessageItem>('/messages', { method: 'POST', body });
}

export function sendMessageNow(id: string): Promise<MessageItem> {
  return api<MessageItem>(`/messages/${id}/send-now`, { method: 'POST', body: {} });
}

export function cancelMessage(id: string): Promise<MessageItem> {
  return api<MessageItem>(`/messages/${id}/cancel`, { method: 'POST', body: {} });
}

// --- appointments ----------------------------------------------------------------------------------

export function useEventTypes(enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.eventTypes(),
    queryFn: () => api<{ items: EventType[] }>('/event-types'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSlots(params: { eventTypeId: string; from: string; to: string } | null) {
  const clean = params ? { ...params, internal: '1' } : {};
  return useQuery({
    queryKey: contactKeys.slots(clean),
    queryFn: ({ signal }) => api<SlotsResponse>(`/slots${query(clean)}`, { signal }),
    enabled: params !== null,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function bookAppointment(body: BookAppointmentBody): Promise<AppointmentDetail> {
  return api<AppointmentDetail>('/appointments', { method: 'POST', body });
}

// --- tasks -----------------------------------------------------------------------------------------

export function useTaskDetail(id: string | null) {
  return useQuery({
    queryKey: contactKeys.task(id ?? ''),
    queryFn: ({ signal }) => api<TaskDetail>(`/tasks/${id ?? ''}`, { signal }),
    enabled: id !== null,
    staleTime: 15_000,
  });
}

/** Users the viewer may assign a task to — `assignees` of „Meine Tasks" (same cache entry as the tasks module). */
export function useAssignees(enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.tasks, 'list', { later: false }] as const,
    queryFn: () =>
      api<{ assignees?: { id: string; name: string; initials: string }[] | undefined }>('/tasks?later=0'),
    enabled,
    staleTime: 60_000,
    select: (data) => data.assignees ?? [],
  });
}

export function createTask(body: CreateTaskBody): Promise<TaskDetail> {
  return api<TaskDetail>('/tasks', { method: 'POST', body });
}

export function snoozeTask(id: string, body: { dueOn: string; dueTime?: string }): Promise<TaskDetail> {
  return api<TaskDetail>(`/tasks/${id}/snooze`, { method: 'POST', body });
}

export function completeTask(
  id: string,
  body: { outcomeType?: string; outcomeText?: string; nextStep?: TaskNextStep },
): Promise<CompleteTaskResponse> {
  return api<CompleteTaskResponse>(`/tasks/${id}/complete`, { method: 'POST', body });
}

// --- documents -------------------------------------------------------------------------------------

export function useContactDocuments(id: string, enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.documents(id),
    queryFn: ({ signal }) => api<DocumentsResponse>(`/documents?contactId=${id}`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

const CUSTOMER_SCOPES: ReadonlySet<string> = new Set(['contact', 'deal', 'financing']);

export function useDocumentTypes(enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.documentTypes(),
    // Customer-side pickers offer the types of a contact, deal or financing (scope, 0032) — no property or
    // rental documents.
    queryFn: async (): Promise<{ items: DocumentType[] }> => {
      const answer = await api<{ items: (DocumentType & { scope: string[] })[] }>('/document-types');
      return {
        items: answer.items
          .filter((type) => type.scope.some((scope) => CUSTOMER_SCOPES.has(scope)))
          .map(({ id, key, name, category }) => ({ id, key, name, category })),
      };
    },
    enabled,
    staleTime: 300_000,
  });
}

export function requestDocuments(body: DocumentRequestBody): Promise<DocumentRequestResponse> {
  return api<DocumentRequestResponse>('/document-requests', { method: 'POST', body });
}

// --- self-disclosure -------------------------------------------------------------------------------

export function useSelfDisclosures(id: string, enabled: boolean) {
  return useQuery({
    queryKey: contactKeys.selfDisclosures(id),
    queryFn: ({ signal }) => api<SelfDisclosuresResponse>(`/self-disclosures?contactId=${id}`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

export function sendSelfDisclosure(body: SendSelfDisclosureBody): Promise<SendSelfDisclosureResponse> {
  return api<SendSelfDisclosureResponse>('/self-disclosures', { method: 'POST', body });
}

export function resendSelfDisclosure(
  id: string,
  body: { channel?: 'email' | 'whatsapp'; to?: string; message?: string },
): Promise<SendSelfDisclosureResponse> {
  return api<SendSelfDisclosureResponse>(`/self-disclosures/${id}/resend`, { method: 'POST', body });
}

/** Refreshes everything a record change concerns (the record, its lists, tasks and documents). */
export function useInvalidateContact(): (id: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (id: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: contactKeys.record(id) }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.contacts] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.tasks] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.documents] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.messages] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.selfDisclosures] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.session] }),
    ]);
  };
}
